import React from 'react';
import { useSelector } from 'react-redux';
import ym from 'react-yandex-metrika';

import { hrLight } from '@edu-frontend/modules/src/components/ui/Theme';
import { MiddleBetaBanner } from '@edu-frontend/modules/src/features/betaBanner';
import { ThemeProvider } from '@edu-frontend/modules/src/shared/lib/ThemeProvider';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { getExperimentFlags, useIntl } from 'platform-components';
import { getMe } from 'platform-components/src/components/user/selectors';
import { useLocalStorage } from 'platform-components/src/utils/hooks/useLocalStorage';
import { getCoursesSubjectsSlugs } from 'store/courses/selectors';
import { getClasses } from 'common.components/user/selectors';

import {
    useBunkerOnboarding,
    TEACHER_MULTI_ASSIGNMENT_ONBOARDING_KEY,
    TEACHER_BETA_LESSONS_ONBOARDING_KEY,
    TEACHER_BETA_CLASSES_ONBOARDING_KEY,
} from 'hooks/useBunkerOnboarding';
import { useBetaFeature } from 'store/beta-features';

type BetaSection = 'materials' | 'lessons' | 'teams';

interface BetaSectionSettings {
    experimentFlag: string;
    requireSubject?: string;
    onboardingId?: string;
}

const BANNER_SETTINGS: Record<BetaSection, BetaSectionSettings> = {
    materials: {
        experimentFlag: 'enableNewMaterials',
        requireSubject: 'informatics',
        onboardingId: TEACHER_MULTI_ASSIGNMENT_ONBOARDING_KEY,
    },
    lessons: {
        experimentFlag: 'enableNewLessons',
        onboardingId: TEACHER_BETA_LESSONS_ONBOARDING_KEY,
    },
    teams: {
        experimentFlag: 'enableNewClasses',
        onboardingId: TEACHER_BETA_CLASSES_ONBOARDING_KEY,
    },
};

/** Отображает баннер беты новых разделов Учебника */
export function BetaBanner(props: { className?: string; section: BetaSection }) {
    const { className, section } = props;
    const intl = useIntl();

    const settings = BANNER_SETTINGS[section];

    const me = useSelector(getMe);
    const activeTeams = useSelector(getClasses);
    const experimentsFlags = useSelector(getExperimentFlags) as Partial<Record<string, number>> | null;
    const subjectSlugs = useSelector(getCoursesSubjectsSlugs);

    const betaMultiAssignment = useBetaFeature('teacherMultiAssignmentEnabled');
    const { onboarding, openOnboarding } = useBunkerOnboarding({ id: settings?.onboardingId ?? '' });

    const hasRequiredSubject =
        !settings.requireSubject ||
        (subjectSlugs.loadingStatus === LOADING_STATUSES.SUCCESS &&
            subjectSlugs.list.some((item) => item === settings.requireSubject));

    const showBanner = Boolean(
        experimentsFlags?.[settings.experimentFlag] === 1 && hasRequiredSubject && activeTeams.length > 0
    );

    const teacherId = me?.id;

    const seenBanner = React.useRef(false);
    React.useEffect(() => {
        if (!seenBanner.current && showBanner) {
            seenBanner.current = true;
            ym('reachGoal', 'teacher_beta_selection_view', { fromSection: section, teacherId });
        }
    }, [section, showBanner, teacherId]);

    const onActivateBeta = React.useCallback(() => {
        betaMultiAssignment.setActive(true);
        ym('reachGoal', 'teacher_beta_multiassignment_on', {
            fromSection: section,
            teacher_id: teacherId,
        });
    }, [section, betaMultiAssignment, teacherId]);

    const storage = useLocalStorage();
    const [collapsed, setCollapsed] = React.useState(storage.get('betaBannerCollapsed'));
    const toggleCollapsed = React.useCallback(
        (nextCollapsed: boolean) => {
            setCollapsed(nextCollapsed);
            storage.set('betaBannerCollapsed', nextCollapsed);
        },
        [storage]
    );

    if (!showBanner) {
        return null;
    }

    return (
        <ThemeProvider isRoot={false} style={{ display: 'contents' }} theme={hrLight}>
            <MiddleBetaBanner
                className={className}
                collapsed={collapsed}
                content={intl.formatMessage({ id: 'betaBanner.generic.content' })}
                leftButton={{
                    content: intl.formatMessage({ id: 'betaBanner.generic.actionActivate' }),
                    isLoading: betaMultiAssignment.setActivePending,
                    onClick: onActivateBeta,
                }}
                onToggleCollapsed={toggleCollapsed}
                rightButton={
                    onboarding
                        ? {
                              content: intl.formatMessage({ id: 'betaBanner.generic.actionLearnMore' }),
                              onClick: () => openOnboarding(),
                          }
                        : undefined
                }
                title={intl.formatMessage({ id: 'betaBanner.generic.title' })}
            />
        </ThemeProvider>
    );
}
